// Thin localStorage shims for the AI agent panel.
//
// Kept separate from `ai-agent.api.js` so the network layer stays pure and
// the panel doesn't touch `window` directly. Each getter returns a sensible
// default when storage is unavailable (SSR, private mode, quota).

const ACCOUNT_KEY = 'ringbaAiAgent.accountId'
const LINKED_KEY = 'ringbaAiAgent.linkedAccounts'

// Saved conversations are namespaced per user so admin1 never reads admin2's
// chats from the same browser. Isolation here is at the app level (the UI only
// ever reads the logged-in user's key); a shared OS account is not a security
// boundary — that would need a server-side store.
const SESSIONS_KEY_PREFIX = 'ringbaAiAgent.sessions.'

// Hard cap so a chatty admin can't fill the ~5MB localStorage quota. Oldest
// sessions fall off the end first.
const MAX_SESSIONS = 25

function safeGet(key) {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function safeSet(key, value) {
  try {
    if (value == null || value === '') {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, value)
    }
  } catch (e) { /* ignore quota/security errors */ }
}

export function getStoredAccountId() {
  return safeGet(ACCOUNT_KEY) || ''
}

export function setStoredAccountId(value) {
  safeSet(ACCOUNT_KEY, value)
}

// Accounts this browser has linked a token for. A UI-side hint only: it gates
// the "Step 1: link account" screen. The worker is the source of truth — it
// re-checks on every query and rejects accounts with no token.
export function getLinkedAccounts() {
  const raw = safeGet(LINKED_KEY)
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(Boolean) : []
  } catch (e) {
    return []
  }
}

export function setLinkedAccounts(list) {
  const arr = Array.isArray(list) ? [...new Set(list.filter(Boolean))] : []
  safeSet(LINKED_KEY, arr.length ? JSON.stringify(arr) : '')
}

function sessionsKey(userId) {
  // Fall back to a shared "anon" bucket only when there's no signed-in user
  // (e.g. before auth resolves); real admins always have an id.
  return SESSIONS_KEY_PREFIX + (userId || 'anon')
}

// Returns the given user's saved sessions, newest first. Always an array, even
// when storage is empty, corrupt, or unavailable.
export function getStoredSessions(userId) {
  const raw = safeGet(sessionsKey(userId))
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed.slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
  } catch (e) {
    return []
  }
}

// Persists the user's sessions (newest first, capped). On a quota error it
// drops the oldest session and retries rather than losing the whole history.
// Returns the list that was actually saved so callers can refresh their view.
export function saveStoredSessions(userId, sessions) {
  const key = sessionsKey(userId)
  let list = (Array.isArray(sessions) ? sessions.slice() : [])
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
    .slice(0, MAX_SESSIONS)

  while (list.length) {
    try {
      localStorage.setItem(key, JSON.stringify(list))
      return list
    } catch (e) {
      // Quota or security error — shed the oldest entry and try again.
      list = list.slice(0, -1)
    }
  }

  try {
    localStorage.removeItem(key)
  } catch (e) { /* ignore */ }

  return []
}
